import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronUp,
  Sparkles,
  Plus,
  Info,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  fetchTopCandidates,
  createApplication,
  type TopCandidate,
  type TopCandidatesResult,
} from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';

interface TopMatchesPanelProps {
  jobId: string;
  jobTitle: string;
}

export function TopMatchesPanel({
  jobId,
  jobTitle,
}: TopMatchesPanelProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Keep this open by default so the ranking panel is always visible.
  const [expanded, setExpanded] = useState(true);
  const [rerank, setRerank] = useState(false);

  const query = useQuery<TopCandidatesResult>({
    queryKey: ['top-candidates', jobId, rerank],
    queryFn: () => fetchTopCandidates(jobId, { limit: 10, rerank }),
    enabled: expanded,
    staleTime: 5 * 60 * 1000,
  });

  const addMutation = useMutation({
    mutationFn: (candidateId: string) =>
      createApplication({ jobId, candidateId, stage: 'sourced' }),
    onSuccess: (_data, candidateId) => {
      addToast({
        type: 'success',
        title: 'Added to pipeline',
        message: 'Candidate placed in Sourced stage.',
      });
      queryClient.invalidateQueries({ queryKey: ['pipeline', jobId] });
      // Refresh top candidates so the added one drops off.
      queryClient.setQueryData<TopCandidatesResult>(
        ['top-candidates', jobId, rerank],
        (prev) =>
          prev
            ? { ...prev, candidates: prev.candidates.filter((c) => c.candidateId !== candidateId) }
            : prev,
      );
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to add candidate';
      addToast({ type: 'error', title: 'Could not add candidate', message });
    },
  });

  const handleRerank = () => {
    setRerank(true);
    queryClient.invalidateQueries({ queryKey: ['top-candidates', jobId, true] });
  };

  const candidates = query.data?.candidates ?? [];

  return (
    <div className="rounded-lg bg-bg-secondary border border-white/5 mb-4">
      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition rounded-t-lg"
      >
        <Sparkles className="w-4 h-4 text-primary-400" />
        <h3 className="font-medium text-text-primary text-sm">
          Top 10 Matches
          <span className="text-text-tertiary ml-2 font-normal">for {jobTitle}</span>
        </h3>
        {query.data?.tier === 'ai_reranked' && !query.data.aiRerankFailed && (
          <Badge variant="secondary" className="text-xs">AI-ranked</Badge>
        )}
        {query.data?.aiRerankFailed && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-400">
            <AlertTriangle className="w-3 h-3" /> AI rerank failed — showing base ranking
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-text-tertiary" />
          ) : (
            <ChevronDown className="w-4 h-4 text-text-tertiary" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-white/5">
          <div className="flex items-center justify-between py-3">
            <p className="text-xs text-text-tertiary flex items-center gap-1">
              <Info className="w-3 h-3" />
              Ranked by skills, experience, location, availability, recency. Click “Add to pipeline” to shortlist.
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRerank}
              disabled={query.isFetching || rerank}
              className="text-xs"
            >
              {query.isFetching && rerank ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-3 h-3 mr-1" />
              )}
              {rerank ? 'Re-ranked with AI' : 'Re-rank with Opus AI'}
            </Button>
          </div>

          {query.isLoading ? (
            <TopMatchesSkeleton />
          ) : query.isError ? (
            <div className="py-6 text-center text-sm text-red-400">
              Failed to load top matches.
            </div>
          ) : candidates.length === 0 ? (
            <div className="py-6 text-center text-sm text-text-tertiary space-y-1">
              {query.data?.warning === 'NEEDS_SKILLS' ? (
                <>
                  <div className="text-amber-400 font-medium">
                    This job has no required skills defined.
                  </div>
                  <div>
                    Matching by job title alone is unreliable. Add required
                    skills to the job (e.g. on the job page → Edit → Skills) to
                    see meaningful matches.
                  </div>
                </>
              ) : query.data?.warning === 'NO_MATCHES' ? (
                <>
                  <div>No candidates in your database match this job's required skills or title.</div>
                  <div>Try loosening skill requirements or importing more candidates.</div>
                </>
              ) : (
                <div>No matching candidates found.</div>
              )}
            </div>
          ) : (
            <ul className="space-y-1">
              {candidates.map((c, idx) => (
                <TopMatchRow
                  key={c.candidateId}
                  rank={idx + 1}
                  candidate={c}
                  onAdd={() => addMutation.mutate(c.candidateId)}
                  isAdding={addMutation.isPending && addMutation.variables === c.candidateId}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function TopMatchRow({
  rank,
  candidate,
  onAdd,
  isAdding,
}: {
  rank: number;
  candidate: TopCandidate;
  onAdd: () => void;
  isAdding: boolean;
}) {
  const name =
    [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || '(Unnamed)';
  const topSkills = candidate.matchedSkills.slice(0, 3);
  const extraSkills = candidate.matchedSkills.length - topSkills.length;

  const scoreColor =
    candidate.matchScore >= 80
      ? 'text-emerald-400'
      : candidate.matchScore >= 60
        ? 'text-primary-400'
        : candidate.matchScore >= 40
          ? 'text-amber-400'
          : 'text-text-tertiary';

  return (
    <li className="flex items-center gap-3 py-2 px-2 rounded hover:bg-white/5 transition">
      <div className="w-6 text-xs text-text-tertiary font-mono">#{rank}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium text-text-primary truncate">{name}</span>
          <span className={cn('text-xs font-semibold', scoreColor)}>
            {candidate.matchScore}%
          </span>
          {candidate.currentJobTitle && (
            <span className="text-xs text-text-tertiary truncate">
              · {candidate.currentJobTitle}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {topSkills.map((skill) => (
            <Badge key={skill} variant="secondary" className="text-[10px] py-0 px-1.5">
              {skill}
            </Badge>
          ))}
          {extraSkills > 0 && (
            <span className="text-[10px] text-text-tertiary">+{extraSkills} more</span>
          )}
          {candidate.aiRationale && (
            <span className="text-[10px] text-text-tertiary italic ml-1 truncate max-w-[240px]">
              “{candidate.aiRationale}”
            </span>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onAdd}
        disabled={isAdding}
        className="text-xs shrink-0"
      >
        {isAdding ? (
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        ) : (
          <Plus className="w-3 h-3 mr-1" />
        )}
        Add to pipeline
      </Button>
    </li>
  );
}

function TopMatchesSkeleton() {
  return (
    <div className="space-y-2 py-2">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-2">
          <div className="w-6 h-3 rounded bg-white/5" />
          <div className="flex-1 space-y-1">
            <div className="h-3 w-1/3 rounded bg-white/5" />
            <div className="h-2 w-1/2 rounded bg-white/5" />
          </div>
          <div className="w-24 h-7 rounded bg-white/5" />
        </div>
      ))}
    </div>
  );
}
